import React, { useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import { Controller, useForm } from 'react-hook-form';
import { format, startOfMonth } from 'date-fns';
import { Bar, Pie, HorizontalBar, Doughnut, Line } from 'react-chartjs-2';
import {
  Card,
  CardBody,
  Row,
  Col,
  Button,
  Form,
  Modal,
  Collapse,
  Input
} from 'reactstrap';
import CustomDashboardService from './service';
import Datepicker from 'components/Inputs/DatePicker';
import FlexSelect from 'components/Inputs/FlexSelect';
import { entitiesName } from 'views/CRM/shared/utils/entitiesName';
import { useRecordTypes } from 'views/RecordType/hooks/useRecordTypes';
import { ErrorMessage } from '@hookform/error-message';
import {
  opportunityFieldsMap,
  dataTypes,
  chartTypes,
  dataPeriod,
  allowedFields
} from './utils/entitiesName';
import { useEntityCustomFieldsByRecordType } from 'views/fieldConfigs/hooks/useCustomFieldsByRecordType';
import CustomFieldsFilterForm from 'views/CRM/shared/components/CustomFieldsFilterForm';
import ToggleSwitch from 'components/ToggleSwitch/ToggleSwitch';
import { getPeriodDates } from './utils/periodDateHelper';
import DefaultFieldsFilterFormOpportunity from './form/DefaultFieldsFilterFormOpportunity';

const dateRules = {
  required: 'Data é obrigatória',
  pattern: {
    value: /^[0-3][0-9]\/[0-1][0-9]\/[0-9][0-9][0-9][0-9]$/,
    message: 'Data inválida! (dd/MM/aaaa)'
  }
};
const getDateBegin = () => {
  const date = new Date();
  const firstDayMonth = new Date(
    date.getFullYear('pt-br'),
    date.getMonth('pt-br'),
    1
  );
  return format(startOfMonth(firstDayMonth), 'dd/LL/yyyy');
};
const toISOFormat = (dateTimeString) => {
  const [date] = dateTimeString.split(' ');
  const [DD, MM, YYYY] = date.split('/');
  return `${DD}/${MM}/${YYYY}`;
};
function generateColors(count) {
  const colors = [];
  for (let i = 0; i < count; i++) {
    const hue = Math.floor((360 / count) * i);
    colors.push(`hsl(${hue}, 70%, 60%)`);
  }
  return colors;
}

const CustomDashboardCard = ({ config, onDelete, onSaved }) => {
  const [selectedCustomFields, setSelectedCustomFields] = useState([]);
  const [filteredCustomFields, setFilteredCustomFields] = useState([]);
  const [selectedRecordType, setSelectedRecordType] = useState('');
  const [autoUpdateEnabled, setAutoUpdateEnabled] = useState(true);
  const [hasAutoSubmitted, setHasAutoSubmitted] = useState(false);
  const [selectedChartType, setSelectedChartType] = useState('');
  const [filterDataOptions, setFilterDataOptions] = useState([]);
  const [customDashboardId, setCustomDashboardId] = useState('');
  const [groupFieldOptions, setGroupFieldOptions] = useState([]);
  const [filterDateBy, setFilterDateBy] = useState('creation');
  const [collapseFilters, setCollapseFilters] = useState(true);
  const [collapseConfig, setCollapseConfig] = useState(true);
  const [selectedEntity, setSelectedEntity] = useState('');
  const [collapseCard, setCollapseCard] = useState(true);
  const [dashboardData, setDashboardData] = useState([]);
  const [fieldOptions, setFieldOptions] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [recordType, setRecordType] = useState([]);
  const [autoUpdate, setAutoUpdate] = useState(30);
  const [loading, setLoading] = useState(false);
  const closeModal = () => setShowModal(false);
  const login = localStorage.getItem('login');
  const openModal = () => setShowModal(true);
  const { recordTypes } = useRecordTypes('');
  const [entity, setEntity] = useState([]);
  const [name, setName] = useState('');
  const [period, setPeriod] = useState('');
  const {
    control,
    handleSubmit,
    setValue,
    getValues,
    errors,
    trigger,
    register,
    reset,
    watch
  } = useForm();
  const { customFields, getCustomFields } = useEntityCustomFieldsByRecordType(
    selectedEntity,
    selectedRecordType
  );
  const fetchDashboard = async (params) => {
    setLoading(true);
    try {
      const { dateBegin, dateEnd } = await getPeriodDates(params.period, {
        dateBegin: params.dateBegin,
        dateEnd: params.dateEnd
      });
      const res = await CustomDashboardService.getCustomDashboardRepository({
        ...params,
        dateBegin,
        dateEnd
      });
      const data = res.data.data || [];
      setDashboardData(data);
      if (!data || data.length === 0) {
        toast.info(
          'Nenhum valor válido encontrado para os filtros selecionados.'
        );
      }
    } catch (err) {
      setDashboardData([]);
      toast.error('Erro ao buscar dados do dashboard.');
    } finally {
      setLoading(false);
    }
  };
  const onSubmit = (data) => {
    fetchDashboard(data);
  };
  const chartData =
    selectedChartType === 'line'
      ? {
          labels: dashboardData.map((item) => item.key),
          datasets: [
            {
              label: 'Quantidade',
              data: dashboardData.map((item) => item.value),
              fill: true,
              backgroundColor: 'rgba(66, 153, 225, 0.2)',
              borderColor: 'rgba(49, 130, 206, 1)',
              pointBackgroundColor: 'rgba(49, 130, 206, 1)',
              pointBorderColor: '#fff'
            }
          ]
        }
      : {
          labels: dashboardData.map((item) => item.key),
          datasets: [
            {
              label: 'Quantidade',
              data: dashboardData.map((item) => item.value),
              backgroundColor: generateColors(dashboardData.length)
            }
          ]
        };
  const renderChart = () => {
    if (selectedChartType === 'column') {
      return <HorizontalBar data={chartData} />;
    }
    if (selectedChartType === 'pie') {
      return <Pie data={chartData} />;
    }
    if (selectedChartType === 'Doughnut') {
      return <Doughnut data={chartData} />;
    }
    if (selectedChartType === 'line') {
      return <Line data={chartData} />;
    }
    return <Bar data={chartData} />;
  };
  const handleSaveConfig = async () => {
    const isValid = await trigger([
      'name',
      'entity',
      'recordType',
      'group',
      'dataField',
      'dataType',
      'chartType',
      'period'
    ]);
    if (!isValid) return;
    const configData = getValues();
    try {
      if (customDashboardId) {
        await CustomDashboardService.updateConfig({
          id: customDashboardId,
          createdBy: login,
          ...configData
        });
        setName(configData.name);
        toast.success('Configurações alteradas com sucesso!');
      } else {
        const res = await CustomDashboardService.saveConfig({
          ...configData,
          createdBy: login
        });
        const newId = res?.data?.data?.id;
        if (typeof onSaved === 'function' && newId) {
          onSaved(newId, res.data.data);
        }
        toast.success('Configurações salvas com sucesso!');
      }
      setCollapseConfig(false);
      setHasAutoSubmitted(false);
    } catch (err) {
      toast.error(
        err.response?.data?.message ||
          err.message ||
          `Erro ao ${customDashboardId ? 'atualizar' : 'criar'} dashboard`
      );
    }
  };

  useEffect(() => {
    if (config) {
      setCollapseConfig(false);
      setCollapseFilters(false);
      reset(config);
      if (config.entity) setSelectedEntity(config.entity);
      if (config.recordType) setSelectedRecordType(config.recordType);
      if (config.id) setCustomDashboardId(config.id);
      if (config.name) setName(config.name);
      if (config.chartType) setSelectedChartType(config.chartType);
      if (config.filters) setSelectedCustomFields(config.filters);
      if (
        config.automaticUpdate &&
        Number(config.automaticUpdate) !== Number(autoUpdate)
      ) {
        setAutoUpdate(Number(config.automaticUpdate));
        setAutoUpdateEnabled(true);
      }
      if (config.period) setPeriod(config.period);
      if (config.filterDateBy) setFilterDateBy(config.filterDateBy);
    }
    return () => {
      if (autoUpdateIntervalId) clearInterval(autoUpdateIntervalId);
    };
  }, [config, setValue]);

  useEffect(() => {
    setHasAutoSubmitted(false);
  }, [config]);

  useEffect(() => {
    const entity = getValues('entity');
    const recordType = getValues('recordType');
    const group = getValues('group');
    const dataField = getValues('dataField');
    const dataType = getValues('dataType');
    const chartType = getValues('chartType');
    const period = getValues('period');
    if (
      !hasAutoSubmitted &&
      entity &&
      recordType &&
      group &&
      dataField &&
      dataType &&
      chartType &&
      period
    ) {
      setHasAutoSubmitted(true);
      handleSubmit(onSubmit)();
      if (autoUpdate > 0 && autoUpdateEnabled) {
        if (autoUpdateIntervalId) clearInterval(autoUpdateIntervalId);
        const interval = setInterval(() => {
          setTimeout(() => handleSubmit(onSubmit)(), 0);
        }, Number(autoUpdate) * 60 * 1000);
        setAutoUpdateIntervalId(interval);
      }
    }
    return () => {
      if (autoUpdateIntervalId) clearInterval(autoUpdateIntervalId);
    };
  }, [
    hasAutoSubmitted,
    autoUpdate,
    autoUpdateEnabled,
    config,
    getValues('entity'),
    getValues('recordType'),
    getValues('group'),
    getValues('dataField'),
    getValues('dataType'),
    getValues('chartType'),
    getValues('period')
  ]);

  const [autoUpdateIntervalId, setAutoUpdateIntervalId] = useState(null);

  useEffect(() => {
    setEntity([
      // { id: entitiesName.ACCOUNT, name: 'Conta' },
      // { id: entitiesName.CONTACT, name: 'Contato' },
      // { id: entitiesName.LEAD, name: 'Lead' },
      { id: entitiesName.OPPORTUNITY, name: 'Oportunidade' }
    ]);
  }, []);

  useEffect(() => {
    if (selectedEntity) {
      const selectedRecordTypes = recordTypes?.filter(
        (recordType) => recordType.entity === selectedEntity
      );

      setRecordType([
        ...selectedRecordTypes?.map((recordType) => ({
          id: recordType.id,
          name: recordType.name
        }))
      ]);
    } else setRecordType([]);
  }, [selectedEntity, entity, recordTypes]);

  useEffect(() => {
    if (selectedEntity && selectedRecordType) {
      getCustomFields();
    }
  }, [selectedEntity, selectedRecordType]);

  useEffect(() => {
    if (selectedEntity === entitiesName.OPPORTUNITY && selectedRecordType) {
      const customFieldOptions = (customFields || []).map((field) => ({
        label: field.label,
        value: field.name
      }));
      const baseOptions = [...opportunityFieldsMap, ...customFieldOptions];
      setFieldOptions(baseOptions);
      // Adiciona weekOfYear apenas para agrupamento
      setGroupFieldOptions([
        ...baseOptions,
        { label: 'Semana do Ano', value: 'weekOfYear' }
      ]);
      const defaultFieldOptions = allowedFields.map((field) => ({
        label: field.label,
        value: field.value,
        type: field.type || 'default',
        ...field
      }));
      setFilterDataOptions([
        ...defaultFieldOptions,
        ...(customFields?.map((field) => ({
          label: field.label,
          value: field.name,
          type: field.type,
          ...field
        })) || [])
      ]);
    } else {
      setFieldOptions([]);
      setGroupFieldOptions([]);
    }
  }, [selectedEntity, customFields, selectedRecordType]);

  useEffect(() => {
    if (
      Array.isArray(selectedCustomFields) &&
      selectedCustomFields.length > 0
    ) {
      const selectedValues = selectedCustomFields.map((f) =>
        typeof f === 'string' ? f : f.value
      );
      const allFields = [
        ...(customFields || []),
        ...allowedFields.filter((field) => selectedValues.includes(field.value))
      ];
      const filtered = allFields.filter((field) =>
        selectedValues.includes(field.name || field.value)
      );
      setFilteredCustomFields(filtered);
    } else {
      setFilteredCustomFields([]);
    }
  }, [selectedCustomFields, customFields]);

  return (
    <Card>
      <Form onSubmit={handleSubmit(onSubmit)}>
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            padding: '12px 16px',
            borderRadius: 8,
            background: '#f8f9fa'
          }}
        >
          <Button
            color="link"
            style={{ marginLeft: 0, padding: '10px 10px' }}
            onClick={() => setCollapseCard((v) => !v)}
          >
            {collapseCard ? (
              <i
                className="fa fa-caret-down"
                style={{ fontSize: 20 }}
                aria-hidden="true"
              ></i>
            ) : (
              <i
                className="fa fa-caret-right"
                style={{ fontSize: 20 }}
                aria-hidden="true"
              ></i>
            )}
          </Button>
          <span
            style={{
              fontWeight: 'bold',
              fontSize: 18,
              flex: 1,
              textAlign: 'left'
            }}
          >
            {collapseConfig ? (
              <Col sm="8">
                <Controller
                  as={
                    <Input
                      type="text"
                      name="name"
                      placeholder="Nome do Dashboard"
                      value={name}
                      valueController={setValue}
                      onChange={(e) => {
                        setName(e.target.value);
                        setValue('name', e.target.value);
                      }}
                    />
                  }
                  control={control}
                  name="name"
                  defaultValue={name || ''}
                  rules={{
                    required: 'O nome é obrigatório!'
                  }}
                />
                <ErrorMessage
                  errors={errors}
                  name="name"
                  render={({ message }) => (
                    <p style={{ color: 'red' }}>{message}</p>
                  )}
                />
              </Col>
            ) : (
              <span
                style={{
                  fontWeight: 'bold',
                  fontSize: 18,
                  flex: 1,
                  textAlign: 'left'
                }}
              >
                {name || 'Sem título'}
                <Button
                  color="link"
                  style={{ marginLeft: 0, padding: '10px 10px' }}
                  type="button"
                  onClick={() => handleSubmit(onSubmit)()}
                >
                  <i
                    className="fa fa-refresh fa-sync"
                    style={{ fontSize: 20 }}
                    aria-hidden="true"
                  ></i>
                </Button>
              </span>
            )}
          </span>

          {collapseConfig ? (
            <>
              <Button
                color="success"
                onClick={handleSaveConfig}
                style={{ marginLeft: 'auto' }}
              >
                {customDashboardId
                  ? 'Alterar Configurações'
                  : 'Salvar Configurações'}
              </Button>
              <Button
                color="danger"
                style={{ marginLeft: 8 }}
                onClick={() => {
                  if (
                    window.confirm('Tem certeza que deseja excluir este card?')
                  ) {
                    if (typeof onDelete === 'function') {
                      onDelete();
                    }
                  }
                }}
              >
                <i
                  className="fa fa-trash"
                  aria-hidden="true"
                  style={{ fontSize: 18 }}
                ></i>
              </Button>
            </>
          ) : null}
          <Button
            color="link"
            onClick={() => {
              setCollapseConfig((v) => !v);
              setCollapseCard(true);
            }}
            style={{ marginLeft: 'auto' }}
          >
            {collapseConfig ? null : (
              <i
                className="fa fa-cog"
                style={{ fontSize: 20 }}
                aria-hidden="true"
              ></i>
            )}
          </Button>
        </div>
        <Collapse isOpen={collapseCard}>
          <CardBody>
            <div
              style={{
                paddingBottom: 10
              }}
            >
              <Collapse isOpen={collapseConfig}>
                <Row>
                  <Col sm="4" className="mb-3">
                    <label className="form-control-label text-capitalize">
                      Entidade <span style={{ color: 'red' }}>*</span>
                    </label>
                    <Controller
                      control={control}
                      name="entity"
                      defaultValue=""
                      render={({ field }) => (
                        <FlexSelect
                          {...field}
                          isClearable={true}
                          dataOptions={entity || []}
                          isMulti={false}
                          closeMenuOnSelect={true}
                          value={selectedEntity}
                          onChange={(selected) => {
                            setSelectedEntity(selected);
                            setValue('entity', selected);
                          }}
                          valueController={setValue}
                          fieldName="entity"
                          labelName="name"
                          valueName="id"
                          defaultValue={''}
                        />
                      )}
                      rules={{
                        required: 'A Entidade é obrigatória!'
                      }}
                    />
                    <ErrorMessage
                      errors={errors}
                      name="entity"
                      render={({ message }) => (
                        <p style={{ color: 'red' }}>{message}</p>
                      )}
                    />
                  </Col>
                  <Col sm="4" className="mb-3">
                    <label className="form-control-label">
                      Tipo de Registro <span style={{ color: 'red' }}>*</span>
                    </label>
                    <Controller
                      control={control}
                      name="recordType"
                      defaultValue=""
                      render={({ field }) => (
                        <FlexSelect
                          {...field}
                          isClearable={true}
                          dataOptions={recordType || []}
                          isMulti={false}
                          closeMenuOnSelect={true}
                          value={selectedRecordType}
                          onChange={(selected) => {
                            setSelectedRecordType(selected);
                            setValue('recordType', selected);
                          }}
                          valueController={setValue}
                          fieldName="recordType"
                          labelName="name"
                          valueName="id"
                          defaultValue={''}
                        />
                      )}
                      rules={{
                        required: 'O Tipo de Registro é obrigatório!'
                      }}
                    />
                    <ErrorMessage
                      errors={errors}
                      name="recordType"
                      render={({ message }) => (
                        <p style={{ color: 'red' }}>{message}</p>
                      )}
                    />
                  </Col>
                  <Col sm="4" className="mb-3">
                    <label className="form-control-label">Filtros</label>
                    <Controller
                      render={() => (
                        <FlexSelect
                          dataOptions={filterDataOptions}
                          isMulti={true}
                          value={selectedCustomFields}
                          onChange={(selected) =>
                            setSelectedCustomFields(selected || [])
                          }
                          valueController={setValue}
                          fieldName="filters"
                          labelName="label"
                          valueName="value"
                          isClearable={true}
                          closeMenuOnSelect={false}
                        />
                      )}
                      control={control}
                      name="filters"
                      defaultValue={[]}
                    />
                  </Col>
                </Row>
                <Row>
                  <Col sm="3" className="mb-3">
                    <label className="form-control-label">
                      Campo de Agrupamento{' '}
                      <span style={{ color: 'red' }}>*</span>
                    </label>
                    <Controller
                      render={(props) => (
                        <FlexSelect
                          dataOptions={groupFieldOptions || []}
                          isMulti={false}
                          value={props.value}
                          valueController={setValue}
                          fieldName="group"
                          labelName="label"
                          valueName="value"
                          isClearable={true}
                          closeMenuOnSelect={true}
                        />
                      )}
                      control={control}
                      name="group"
                      defaultValue=""
                      rules={{
                        required: 'O Campo de Agrupamento é obrigatório!'
                      }}
                    />
                    <ErrorMessage
                      errors={errors}
                      name="group"
                      render={({ message }) => (
                        <p style={{ color: 'red' }}>{message}</p>
                      )}
                    />
                  </Col>
                  <Col sm="3" className="mb-3">
                    <label className="form-control-label">
                      Campo de Dado <span style={{ color: 'red' }}>*</span>
                    </label>
                    <Controller
                      render={(props) => (
                        <FlexSelect
                          dataOptions={fieldOptions || []}
                          isMulti={false}
                          value={props.value}
                          valueController={setValue}
                          fieldName="dataField"
                          labelName="label"
                          valueName="value"
                          isClearable={true}
                          closeMenuOnSelect={true}
                        />
                      )}
                      control={control}
                      name="dataField"
                      defaultValue=""
                      rules={{
                        required: 'O Campo de Dado é obrigatório!'
                      }}
                    />
                    <ErrorMessage
                      errors={errors}
                      name="dataField"
                      render={({ message }) => (
                        <p style={{ color: 'red' }}>{message}</p>
                      )}
                    />
                  </Col>
                  <Col sm="3" className="mb-3">
                    <label className="form-control-label">
                      Tipo do Campo de Dados
                      <span style={{ color: 'red' }}>*</span>
                    </label>
                    <Controller
                      render={(props) => (
                        <FlexSelect
                          isClearable={true}
                          dataOptions={dataTypes}
                          isMulti={false}
                          closeMenuOnSelect={true}
                          value={props.value}
                          valueController={setValue}
                          fieldName="dataType"
                          labelName="label"
                          valueName="value"
                        />
                      )}
                      control={control}
                      name="dataType"
                      rules={{
                        required: 'O Tipo do Campo de Dados é obrigatório!'
                      }}
                    />
                    <ErrorMessage
                      errors={errors}
                      name="dataType"
                      render={({ message }) => (
                        <p style={{ color: 'red' }}>{message}</p>
                      )}
                    />
                  </Col>
                  <Col sm="3" className="mb-3">
                    <label className="form-control-label">
                      Tipo de Visualização{' '}
                      <span style={{ color: 'red' }}>*</span>
                    </label>
                    <Controller
                      render={(props) => (
                        <FlexSelect
                          isClearable={true}
                          dataOptions={chartTypes}
                          isMulti={false}
                          closeMenuOnSelect={true}
                          value={props.value}
                          valueController={setValue}
                          onChange={(selected) => {
                            setSelectedChartType(selected);
                            setValue('chartType', selected);
                          }}
                          fieldName="chartType"
                          labelName="label"
                          valueName="value"
                        />
                      )}
                      control={control}
                      name="chartType"
                      defaultValue={chartTypes[0].value}
                      rules={{
                        required: 'O Tipo de Visualização é obrigatório!'
                      }}
                    />
                    <ErrorMessage
                      errors={errors}
                      name="chartType"
                      render={({ message }) => (
                        <p style={{ color: 'red' }}>{message}</p>
                      )}
                    />
                  </Col>
                </Row>
                <Row>
                  <Col sm="2" className="mb-3">
                    <label className="form-control-label">
                      Período
                      <span style={{ color: 'red' }}>*</span>
                    </label>
                    <Controller
                      render={(props) => (
                        <FlexSelect
                          isClearable={true}
                          dataOptions={dataPeriod || []}
                          isMulti={false}
                          closeMenuOnSelect={true}
                          value={props.value}
                          valueController={setValue}
                          onChange={(selected) => {
                            setPeriod(selected);
                            setValue('period', selected);
                          }}
                          fieldName="period"
                          labelName="label"
                          valueName="value"
                        />
                      )}
                      control={control}
                      name="period"
                      defaultValue=""
                      rules={{
                        required: 'Período é obrigatório!'
                      }}
                    />
                    <ErrorMessage
                      errors={errors}
                      name="period"
                      render={({ message }) => (
                        <p style={{ color: 'red' }}>{message}</p>
                      )}
                    />
                  </Col>
                  {period === 'dateRange' && (
                    <>
                      <Col sm="2" className="mb-3">
                        <label className="form-control-label">
                          Data inicial
                        </label>
                        <Controller
                          render={(props) => (
                            <Datepicker
                              fieldName="dateBegin"
                              valueController={setValue}
                              defaultValue={props.value}
                            />
                          )}
                          control={control}
                          name="dateBegin"
                          rules={dateRules}
                          defaultValue={
                            getValues('dateBegin') || getDateBegin()
                          }
                        />
                      </Col>
                      <Col sm="2" className="mb-3">
                        <label className="form-control-label">Data final</label>
                        <Controller
                          render={(props) => (
                            <Datepicker
                              fieldName="dateEnd"
                              valueController={setValue}
                              defaultValue={props.value}
                            />
                          )}
                          control={control}
                          name="dateEnd"
                          defaultValue={new Date().toLocaleDateString('pt-br')}
                          rules={{
                            ...dateRules,
                            validate: async (value) => {
                              if (
                                new Date(toISOFormat(value)) <
                                new Date(toISOFormat(getValues()['dateBegin']))
                              ) {
                                return 'A data final não pode ser anterior à inicial!';
                              } else {
                                return true;
                              }
                            }
                          }}
                        />
                      </Col>
                    </>
                  )}
                  {['today', 'thisWeek', 'thisMonth', 'thisQuarter'].includes(
                    period
                  ) && (
                    <Col sm="3" className="mb-3">
                      <label
                        className="form-control-label"
                        style={{
                          marginLeft: '-15px',
                          marginBottom: 0,
                          height: 30
                        }}
                      >
                        <ToggleSwitch
                          id="showDateForm"
                          checked={autoUpdateEnabled}
                          onChange={() =>
                            setAutoUpdateEnabled(!autoUpdateEnabled)
                          }
                          default={false}
                        />
                        Ativar atualização automática
                      </label>
                      <div
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          margin: 0
                        }}
                      >
                        <Controller
                          name="automaticUpdate"
                          control={control}
                          defaultValue={autoUpdate}
                          render={(props) => (
                            <Input
                              type="number"
                              min={10}
                              max={120}
                              step={1}
                              value={autoUpdateEnabled ? props.value : ''}
                              onChange={(e) => {
                                const value = e.target.value;
                                props.onChange(value);
                                setAutoUpdate(value);
                              }}
                              onBlur={(e) => {
                                let value = parseInt(e.target.value);
                                if (isNaN(value) || value < 10) value = 10;
                                if (value > 120) value = 120;
                                setAutoUpdate(value);
                                props.onChange(value);
                              }}
                              style={{ width: 70, marginRight: 8 }}
                              disabled={!autoUpdateEnabled}
                            />
                          )}
                        />
                        <span style={{ fontWeight: 400 }}>minutos</span>
                      </div>
                      {errors.automaticUpdate && (
                        <p style={{ color: 'red' }}>
                          {errors.automaticUpdate.message}
                        </p>
                      )}
                    </Col>
                  )}
                </Row>
                {selectedEntity === entitiesName.OPPORTUNITY && (
                  <Row className="mb-3">
                    <Col sm="12">
                      <label
                        className="form-control-label"
                        style={{ marginRight: '20px', fontWeight: '400' }}
                      >
                        <input
                          type="radio"
                          name="filterDateBy"
                          ref={register}
                          value="creation"
                          defaultChecked={filterDateBy === 'creation'}
                        />{' '}
                        Data de criação
                      </label>
                      <label
                        className="form-control-label"
                        style={{ fontWeight: '400' }}
                      >
                        <input
                          type="radio"
                          name="filterDateBy"
                          ref={register}
                          value="last_update"
                          defaultChecked={filterDateBy === 'last_update'}
                        />{' '}
                        Data de última alteração
                      </label>
                      <label
                        className="form-control-label"
                        style={{ fontWeight: '400', marginLeft: '20px' }}
                      >
                        <input
                          type="radio"
                          ref={register}
                          name="filterDateBy"
                          value="closed"
                          defaultChecked={filterDateBy === 'closed'}
                        />{' '}
                        Data de fechamento
                      </label>
                    </Col>
                  </Row>
                )}
              </Collapse>
            </div>
            <div
              style={{
                marginBottom: 16,
                paddingBottom: 10
              }}
            >
              <Button
                color="link"
                style={{ marginLeft: 0, padding: '10px 10px' }}
                onClick={() => setCollapseFilters((v) => !v)}
              >
                {collapseFilters ? (
                  <i
                    className="fa fa-caret-down"
                    style={{ fontSize: 20, color: '#868585ff' }}
                    aria-hidden="true"
                  ></i>
                ) : (
                  <i
                    className="fa fa-caret-right"
                    style={{ fontSize: 20, color: '#868585ff' }}
                    aria-hidden="true"
                  ></i>
                )}
              </Button>
              <span
                style={{
                  fontWeight: 'bold',
                  fontSize: 16
                }}
              >
                Filtros
              </span>
              <Collapse isOpen={collapseFilters}>
                <Row className="mb-3">
                  <Col>
                    <DefaultFieldsFilterFormOpportunity
                      defaultFields={filteredCustomFields}
                      control={control}
                      setValue={setValue}
                      recordTypeId={selectedRecordType}
                      getValues={getValues}
                    />
                  </Col>
                </Row>
                <Row className="mb-3">
                  <Col>
                    <CustomFieldsFilterForm
                      customFields={filteredCustomFields}
                      control={control}
                      setValue={setValue}
                      toISOFormat={toISOFormat}
                      getValues={getValues}
                      watch={watch}
                    />
                  </Col>
                </Row>
              </Collapse>
            </div>
            {loading ? (
              <div
                style={{
                  width: '100%',
                  textAlign: 'center',
                  paddingTop: 120
                }}
              >
                <span
                  className="spinner-border"
                  role="status"
                  aria-hidden="true"
                  style={{ fontSize: 32, color: '#888' }}
                ></span>
                <div style={{ marginTop: 16, color: '#888' }}>
                  Carregando dados...
                </div>
              </div>
            ) : (
              <Row className="mt-4">
                <Col md={8}>
                  <div
                    style={{
                      position: 'relative',
                      height: '400px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      background: '#fff'
                    }}
                  >
                    <Button
                      color="secondary"
                      size="sm"
                      style={{
                        position: 'absolute',
                        top: 10,
                        right: 10,
                        zIndex: 2
                      }}
                      onClick={openModal}
                    >
                      <i className="fas fa-expand"></i>
                    </Button>
                    <div style={{ width: '100%', height: '100%' }}>
                      {renderChart()}
                    </div>
                  </div>
                  <Modal
                    isOpen={showModal}
                    toggle={closeModal}
                    size="xl"
                    style={{
                      marginLeft: '12vw',
                      background: '#fff',
                      borderRadius: 8
                    }}
                  >
                    <div
                      style={{
                        width: '75vw',
                        height: '90vh',
                        maxWidth: '75vw',
                        maxHeight: '90vh',
                        background: '#fff',
                        borderRadius: 8,
                        display: 'flex',
                        flexDirection: 'column',
                        boxShadow: '0 0 24px rgba(0,0,0,0.1)',
                        margin: 'auto'
                      }}
                    >
                      <div
                        style={{
                          padding: '10px',
                          color: '#222',
                          fontWeight: 'bold',
                          fontSize: 18,
                          borderRadius: '8px 8px 0 0',
                          background: '#fff',
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'center'
                        }}
                      >
                        <span>Visualização do Gráfico</span>
                        <Button
                          color="link"
                          style={{ color: '#222', fontSize: 22 }}
                          onClick={closeModal}
                        >
                          &times;
                        </Button>
                      </div>
                      <div
                        style={{
                          flex: 1,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          background: '#f7f7f7',
                          margin: '10px',
                          minHeight: '70vh',
                          borderRadius: 8
                        }}
                      >
                        {renderChart()}
                      </div>
                    </div>
                  </Modal>
                </Col>
                <Col md={4}>
                  <div
                    style={{
                      maxHeight: '400px',
                      overflowY: 'auto',
                      overflowX: 'auto',
                      margin: '0 auto',
                      position: 'relative'
                    }}
                  >
                    <table
                      className="table table-bordered"
                      style={{ width: '100%', borderCollapse: 'collapse' }}
                    >
                      <thead>
                        <tr>
                          <th
                            style={{
                              position: 'sticky',
                              top: 0,
                              background: '#fff',
                              zIndex: 2
                            }}
                          >
                            {(() => {
                              const groupField = (groupFieldOptions || []).find(
                                (f) => f.value === getValues('group')
                              );
                              return groupField
                                ? groupField.label
                                : 'Agrupamento';
                            })()}
                          </th>
                          <th
                            style={{
                              position: 'sticky',
                              top: 0,
                              background: '#fff',
                              zIndex: 2
                            }}
                          >
                            Quantidade
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        {dashboardData.map((item) => (
                          <tr key={item.key}>
                            <td>{item.key || 'null'}</td>
                            <td>
                              {typeof item.value === 'number'
                                ? Number.isInteger(item.value)
                                  ? new Intl.NumberFormat('pt-BR', {
                                      minimumFractionDigits: 0,
                                      maximumFractionDigits: 0
                                    }).format(item.value)
                                  : new Intl.NumberFormat('pt-BR', {
                                      minimumFractionDigits: 2,
                                      maximumFractionDigits: 2
                                    }).format(item.value)
                                : item.value}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                      <tfoot>
                        <tr>
                          <td
                            style={{
                              position: 'sticky',
                              bottom: 0,
                              background: '#f8f9fa',
                              fontWeight: 'bold',
                              zIndex: 2
                            }}
                          >
                            Total
                          </td>
                          <td
                            style={{
                              position: 'sticky',
                              bottom: 0,
                              background: '#f8f9fa',
                              fontWeight: 'bold',
                              zIndex: 2
                            }}
                          >
                            {(() => {
                              const total = dashboardData.reduce(
                                (acc, item) =>
                                  acc +
                                  (typeof item.value === 'number'
                                    ? item.value
                                    : 0),
                                0
                              );
                              return Number.isInteger(total)
                                ? new Intl.NumberFormat('pt-BR', {
                                    minimumFractionDigits: 0,
                                    maximumFractionDigits: 0
                                  }).format(total)
                                : new Intl.NumberFormat('pt-BR', {
                                    minimumFractionDigits: 2,
                                    maximumFractionDigits: 2
                                  }).format(total);
                            })()}
                          </td>
                        </tr>
                      </tfoot>
                    </table>
                  </div>
                </Col>
              </Row>
            )}
          </CardBody>
        </Collapse>
      </Form>
    </Card>
  );
};

export default CustomDashboardCard;
